/** @format */
import { Button } from '@mpb-com/feather/components/atoms/button';
import { ButtonVariant } from '@mpb-com/feather/components/atoms/button/types';
import { ContentContainer } from '@mpb-com/feather/components/atoms/content-container';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingColor,
    HeadingSize,
    HighlightColor,
} from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { animated } from '@react-spring/web';
import { Trans, useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';

import ChevronLeft from '@toucan/client/global/icons/chevron-left.svg';
import ChevronRight from '@toucan/client/global/icons/chevron-right.svg';

import {
    CarouselStyle,
    useCarousel,
} from '@toucan/client/shared/hooks/use-carousel';

import { FeaturedArticleCard } from './components/featured-article-card';
import { FeaturedArticlesBlockProps } from './config/types';
import { styles } from './featured-articles-block.styles';

export type {
    FeaturedArticle,
    FeaturedArticlesBlockProps,
} from './config/types';

const StyledFeaturedArticlesBlock = styled.section`
    ${styles}
`;

export const FeaturedArticlesBlock = ({
    category,
    categoryHref,
    articles,
}: FeaturedArticlesBlockProps) => {
    const { t } = useTranslation();

    const {
        carouselRef,
        handlePrev,
        handleNext,
        isPrevDisabled,
        isNextDisabled,
        style,
    } = useCarousel<HTMLUListElement>({
        itemLength: articles.length,
        carouselStyle: CarouselStyle.SLIDE,
    });

    return (
        <StyledFeaturedArticlesBlock className="featured-articles-block">
            <ContentContainer>
                <div className="featured-articles-block__container">
                    <Heading
                        size={HeadingSize.H2}
                        color={HeadingColor.TEXT_SECONDARY}
                        highlightColor={HighlightColor.TEXT_ACCENT}
                        className="featured-articles-block__title"
                    >
                        <Trans components={{ highlighted: <strong /> }}>
                            {t('<highlighted>Featured</highlighted> articles')}
                        </Trans>
                    </Heading>
                    <Button
                        className="featured-articles-block__see-all"
                        variant={ButtonVariant.SECONDARY}
                        href={categoryHref}
                    >
                        {t('More {{category}}', { category })}
                    </Button>
                    <animated.ul
                        ref={carouselRef}
                        className="featured-articles-block__carousel"
                        style={style}
                    >
                        {articles.map((article) => (
                            <FeaturedArticleCard
                                key={article.link.href}
                                {...article}
                            />
                        ))}
                    </animated.ul>
                    <div className="featured-articles-block__navigation">
                        <Icon
                            size={IconSize.M}
                            variant={IconVariant.ACTIONABLE}
                            aria-label={t('Previous')}
                            data-testid="featured-articles-block__prev-arrow"
                            onClick={handlePrev}
                            disabled={isPrevDisabled}
                        >
                            <ChevronLeft />
                        </Icon>
                        <Icon
                            size={IconSize.M}
                            variant={IconVariant.ACTIONABLE}
                            aria-label={t('Next')}
                            data-testid="featured-articles-block__next-arrow"
                            onClick={handleNext}
                            disabled={isNextDisabled}
                        >
                            <ChevronRight />
                        </Icon>
                    </div>
                </div>
            </ContentContainer>
        </StyledFeaturedArticlesBlock>
    );
};
